import * as React from 'react';

/**
 * FormLayout — SSOT pro formulare ve dvou sloupcich (krok UXK2, vzor Nová
 * zakázka / Nová smlouva: `.rh-a2-cols--main`). `main` jsou karty formulare,
 * `aside` je uzky pravy panel (souhrn, co se stane po ulozeni) - kdyz chybi,
 * formular zabira celou sirku. `actions` je prilepena spodni lista s hlavni
 * akci (ne tlacitko uprostred formulare, R.B. k Nove fakture).
 */
export interface FormLayoutProps {
  readonly main: React.ReactNode;
  readonly aside?: React.ReactNode;
  /**
   * Karty, ktere se do uzkeho leveho sloupce nevejdou — stoji pod obema
   * sloupci a maji CELOU sirku formulare (krok UXD2).
   *
   * Duvod: `main` je 1.6fr z ~1040 px, tedy ~625 px. Tabulka polozek faktury
   * potrebuje v nejuzsim citelnem stavu ~910 px (UXP4a: zadne pole pod 80 px),
   * takze v levem sloupci se musela rolovat vodorovne — presne nalez, ktery
   * rucni pruchod na 1440 px hlasil. `aside` (souhrn) je `sticky`, takze
   * zustava videt i kdyz je pod nim dalsi karta.
   */
  readonly wide?: React.ReactNode;
  readonly actions?: React.ReactNode;
  /** `limited` = faktura (~1040px), `narrow` = kontakt (~720px). */
  readonly width?: 'limited' | 'narrow' | 'full';
  readonly className?: string;
  readonly testId?: string;
}

export function FormLayout({ main, aside, wide, actions, width = 'full', className, testId }: FormLayoutProps): React.JSX.Element {
  const widthClass = width === 'limited' ? ' rh-a2-form-layout--limited' : width === 'narrow' ? ' rh-a2-form-layout--narrow' : '';
  return (
    <div className={`rh-a2-form-layout${widthClass}${className ? ` ${className}` : ''}`} data-testid={testId}>
      {aside !== undefined ? (
        <div className="rh-a2-cols rh-a2-cols--main">
          <div className="rh-a2-stack">{main}</div>
          <aside className="rh-a2-stack rh-a2-form-aside">{aside}</aside>
        </div>
      ) : (
        <div className="rh-a2-stack">{main}</div>
      )}
      {wide !== undefined && (
        <div className="rh-a2-stack rh-a2-form-wide" data-testid={testId === undefined ? undefined : `${testId}-wide`}>
          {wide}
        </div>
      )}
      {actions !== undefined && <div className="rh-a2-formbar">{actions}</div>}
    </div>
  );
}

/** Pole formulare ve dvou sloupcich (jeden pod mobilnim zlomem ~900px). */
export function FieldGrid({
  children,
  columns,
  className,
}: {
  readonly children: React.ReactNode;
  /** CSS `grid-template-columns`, napr. `'2fr 1fr 1fr'` pro ulice/obec/PSC. */
  readonly columns?: string;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`rh-a2-field-grid${className ? ` ${className}` : ''}`}
      style={columns !== undefined ? ({ '--rh-a2-field-grid-cols': columns } as React.CSSProperties) : undefined}
    >
      {children}
    </div>
  );
}
